import React from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  useNotify,
} from 'core/hooks';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  RenderingProviderDTO,
} from 'dtos';
import {
  getSubmitButtonText,
  isEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useUpdateRenderingProvider,
} from 'views/EMR/Settings/hooks';
import NPI from 'views/EMR/Settings/BillingConfigurations/NPI';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import ToolTip from 'core/components/Tooltip';

export type RenderingProviderDialogProps = {
  provider?: RenderingProviderDTO | null;
};

type Props = RenderingProviderDialogProps & {
  onClose: () => void;
};

const defaultValues: RenderingProviderDTO = {
  firstName: '',
  lastName: '',
  credentials: '',
  isPrincipal: false,
  npi: {
    number: '',
    taxonomyCode: '',
  },
};

export default function RenderingProviderDialog({
  onClose,
  provider,
}: Props) {
  const isNew = isNil(provider);
  const formProps = useForm<RenderingProviderDTO>({
    mode: 'onChange',
    defaultValues: provider ?? defaultValues,
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
  } = formProps;

  const { mutateAsync: updateRenderingProvider } = useUpdateRenderingProvider();
  const notify = useNotify();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async ({ credentials, ...data }: RenderingProviderDTO) => {
    try {
      await updateRenderingProvider({
        ...data,
        credentials: isEmptyString(credentials) ? undefined : credentials,
      });
      notify({
        variant: 'success',
        message: `Rendering Provider ${isNew ? 'created' : 'updated'} successfully`,
      });
      settingsEventChannel.emit('onRenderingProviderChange');
      handleClose();
    } finally {
      reset(data, { keepIsSubmitted: false });
    }
  };

  return (
    <FormProvider {...formProps}>
      <Dialog
        title={(
          <div className="flex flex-1 flex-col gap-4">
            <h2 className="text-lg font-semibold">
              {`${isNew ? 'Add' : 'Edit'} Rendering Provider`}
            </h2>
          </div>
        )}
        closeBtnClasses="!-top-1"
        onClose={onClose}
        onConfirm={noop}
        className="!w-176 populate-form"
        maxWidth="lg"
        open
      >
        <div className="flex flex-col gap-form-row">
          <div className="flex flex-col gap-form-row">
            <div className="flex gap-form-field">
              <Controller
                name="firstName"
                control={control}
                rules={{ required: 'First Name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    error={!isNil(error)}
                    showErrorText={false}
                    className="w-full text-gray-500 text-xs"
                    id="first-name"
                    label="First Name"
                    placeholder="First Name"
                    inputProps={{ maxLength: 64 }}
                    required
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                rules={{ required: 'Last Name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    error={!isNil(error)}
                    showErrorText={false}
                    className="w-full text-gray-500 text-xs"
                    id="last-name"
                    label="Last Name"
                    placeholder="Last Name"
                    inputProps={{ maxLength: 64 }}
                    required
                  />
                )}
              />
              <Controller
                name="credentials"
                control={control}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    className="w-full text-gray-500 text-xs"
                    id="credentials-name"
                    label="Credentials"
                    placeholder="E.g. DPM"
                    inputProps={{ maxLength: 8 }}
                  />
                )}
              />
            </div>
            <Controller
              name="isPrincipal"
              control={control}
              render={({ field }) => (
                <div className="w-20">
                  <ToolTip title="Contact support to change the principal Rendering Provider.">
                    <span>
                      <Checkbox
                        {...field}
                        id="is-principal-toggle"
                        checked={Boolean(field.value)}
                        value={(field?.value ?? '').toString()}
                        label="Is Principal"
                        disabled
                      />
                    </span>
                  </ToolTip>
                </div>
              )}
            />
            <NPI />
          </div>
          <div className="flex gap-2 justify-between">
            <Button
              variant="outlined"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {getSubmitButtonText(isNew, isSubmitting)}
              </Button>
            </DisabledFormTooltip>
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
}
